import React, { useMemo, useState } from "react";

/**
 * FUTUREOIL 2026 — AI, DATA & NET-ZERO ENERGY SHIFT CONFERENCE
 * April 21–22, 2026 | 100% Virtual
 * Source: Future Oil 2026 Agenda.pdf :contentReference[oaicite:0]{index=0}
 *
 * Changes requested:
 * ✅ Add speaker images (like brochure)
 * ✅ Remove LOGO block
 * ✅ Keep full-width bars/cards and same design system
 * ✅ Keep speakers + titles + companies accurate (per brochure)
 */

const Agenda = () => {
  const [activeDay, setActiveDay] = useState("day1");

  const meta = {
    pageTitle: "OFFICIAL AGENDA 2026",
    downloadLabel: "Download Agenda PDF",
    downloadUrl: "assets/images/speakers/mypdf.pdf",
    locationNote: "Event will take place in Houston, TX, USA (GMT-5) time zone.",
  };

  const data = useMemo(() => {
    const STATUS = {
      SPONSORED: "SPONSORED",
      BOOKED: "BOOKED",
      AVAILABLE: "AVAILABLE",
      BREAK: "BREAK",
    };

    /**
     * Speaker images (YOU must place these files in /public/images/ exactly)
     * If your filenames are different, change them here only.
     */
    const SPEAKER_IMAGES = {
      "Priya Bennett": "assets/images/speakers/agenda1.png",
      "Ahmed Khan": "assets/images/speakers/3.png",
      "Jonathan Blake": "assets/images/speakers/agenda2.png",
      "Dr. Anika Williams": "assets/images/speakers/1.png",
      "Sophie Dubois": "assets/images/speakers/agenda4.png",
      "Yuki Tanaka": "assets/images/speakers/agenda3.png",
      "Dr. Liam O’Connor": "assets/images/speakers/agenda2.png",
      "Ananya Desai": "assets/images/speakers/agenda5.png",
    };

    const day1 = {
      id: "day1",
      label: "DAY 1",
      dateLabel: "Wednesday, 21 April 2026",
      legend: [
        { label: "Sponsored Sessions", type: STATUS.SPONSORED },
        { label: "Booked Sessions", type: STATUS.BOOKED },
        { label: "Available Session", type: STATUS.AVAILABLE },
      ],
      items: [
        { type: "session", status: STATUS.BOOKED, time: "09:15 - 09:30", title: "Welcome Address & Conference Overview" },
        { type: "session", status: STATUS.AVAILABLE, time: "09:30 - 10:00", title: "Available Session" },
        { type: "session", status: STATUS.AVAILABLE, time: "10:00 - 10:30", title: "Available Session" },
        { type: "session", status: STATUS.AVAILABLE, time: "10:30 - 11:00", title: "Available Session" },
        { type: "session", status: STATUS.AVAILABLE, time: "11:00 - 11:30", title: "Available Session" },
        { type: "session", status: STATUS.AVAILABLE, time: "11:30 - 12:00", title: "Available Session" },

        { type: "break", status: STATUS.BREAK, time: "12:00 - 13:00", title: "LUNCH & NETWORKING BREAK", sub: "[1 Hour]" },

        {
          type: "session",
          status: STATUS.BOOKED,
          time: "13:00 - 13:30",
          title: "AI-Enhanced Reservoir Optimization & Production Forecasting",
          bullets: [
            "Using advanced analytics to forecast reservoir behavior and improve recovery efficiency.",
            "Reducing uncertainty in field development through AI-driven modeling.",
            "Enhancing operational reliability with predictive insights across upstream portfolios.",
          ],
          speaker: {
            name: "Priya Bennett",
            role: "Chief Reservoir Engineer",
            company: "BlackGold Innovations",
            img: SPEAKER_IMAGES["Priya Bennett"],
          },
        },
        {
          type: "session",
          status: STATUS.BOOKED,
          time: "13:30 - 14:00",
          title: "Digital Transformation Frameworks for Global Energy Operations",
          bullets: [
            "Implementing scalable digital programs that enhance operational efficiency across diverse assets.",
            "Integrating AI, automation, and real-time analytics to modernize traditional workflow.",
            "Overcoming organizational barriers to accelerate digital maturity.",
          ],
          speaker: {
            name: "Ahmed Khan",
            role: "Director",
            company: "Global Oil Solutions",
            img: SPEAKER_IMAGES["Ahmed Khan"],
          },
        },
        {
          type: "session",
          status: STATUS.BOOKED,
          time: "14:00 - 14:30",
          title: "Strategic Pathways for AI Adoption in the Energy Transition",
          bullets: [
            "Aligning digital strategy with corporate sustainability goals.",
            "Enhancing operational performance through AI-driven decision frameworks.",
            "Positioning organizations for competitive advantage in evolving energy markets.",
          ],
          speaker: {
            name: "Jonathan Blake",
            role: "VP Strategy",
            company: "CoreGas Inc.",
            img: SPEAKER_IMAGES["Jonathan Blake"],
          },
        },

        { type: "break", status: STATUS.BREAK, time: "14:30 - 15:00", title: "NETWORKING BREAK", sub: "[30 Minutes]" },

        {
          type: "session",
          status: STATUS.BOOKED,
          time: "15:00 - 15:30",
          title: "Data-Driven Sustainability & Emissions Intelligence",
          bullets: [
            "How digital platforms transform ESG reporting and compliance.",
            "Leveraging analytics to reduce emissions and meet net-zero mandates.",
            "Building transparent, scalable sustainability frameworks.",
          ],
          speaker: {
            name: "Dr. Anika Williams",
            role: "Energy Policy Advisor",
            company: "CoreGas Inc.",
            img: SPEAKER_IMAGES["Dr. Anika Williams"],
          },
        },

        { type: "session", status: STATUS.AVAILABLE, time: "15:30 - 16:00", title: "Available Session" },
        { type: "session", status: STATUS.BOOKED, time: "16:00 - 16:30", title: "Day 1 Closing Remarks" },
      ],
    };

    const day2 = {
      id: "day2",
      label: "DAY 2",
      dateLabel: "Thursday, 22 April 2026",
      legend: [
        { label: "Sponsored Sessions", type: STATUS.SPONSORED },
        { label: "Booked Sessions", type: STATUS.BOOKED },
        { label: "Available Session", type: STATUS.AVAILABLE },
      ],
      items: [
        { type: "session", status: STATUS.AVAILABLE, time: "09:30 - 10:00", title: "Available Session" },
        { type: "session", status: STATUS.AVAILABLE, time: "10:00 - 10:30", title: "Available Session" },
        { type: "session", status: STATUS.AVAILABLE, time: "10:30 - 11:00", title: "Available Session" },
        { type: "session", status: STATUS.AVAILABLE, time: "11:00 - 11:30", title: "Available Session" },
        { type: "session", status: STATUS.AVAILABLE, time: "11:30 - 12:00", title: "Available Session" },

        { type: "break", status: STATUS.BREAK, time: "12:00 - 13:00", title: "LUNCH & NETWORKING BREAK", sub: "[1 Hour]" },

        {
          type: "session",
          status: STATUS.BOOKED,
          time: "13:00 - 13:30",
          title: "Digital Twins & Intelligent Asset Performance at Scale",
          bullets: [
            "How enterprise digital twins unify asset visibility.",
            "Predictive intelligence for reducing operational risks.",
            "Optimizing decision-making with remote operational insights.",
          ],
          speaker: {
            name: "Sophie Dubois",
            role: "Chief Technology Officer",
            company: "PetroAI",
            img: SPEAKER_IMAGES["Sophie Dubois"],
          },
        },
        {
          type: "session",
          status: STATUS.BOOKED,
          time: "13:30 - 14:00",
          title: "Building Next-Generation Digital Twin Ecosystems",
          bullets: [
            "Deploying scalable twin architectures for complex infrastructures.",
            "Enhancing asset life cycle management through simulation.",
            "Driving operational excellence with AI-integrated twins.",
          ],
          speaker: {
            name: "Yuki Tanaka",
            role: "VP of Digital Twins",
            company: "JERA",
            img: SPEAKER_IMAGES["Yuki Tanaka"],
          },
        },
        {
          type: "session",
          status: STATUS.BOOKED,
          time: "14:00 - 14:30",
          title: "Geothermal Innovation & Low-Carbon Energy Systems",
          bullets: [
            "Harnessing geothermal potential in the clean-energy landscape.",
            "Innovative subsurface modeling techniques powered by AI.",
            "Integrating geothermal into hybrid net-zero energy portfolios.",
          ],
          speaker: {
            name: "Dr. Liam O’Connor",
            role: "Director of Geothermal Innovation",
            company: "BP",
            img: SPEAKER_IMAGES["Dr. Liam O’Connor"],
          },
        },

        { type: "session", status: STATUS.AVAILABLE, time: "14:30 - 15:00", title: "Available Session" },

        {
          type: "session",
          status: STATUS.BOOKED,
          time: "15:00 - 15:30",
          title: "AI Governance & Enterprise Data Strategy in Energy",
          bullets: [
            "Building secure and responsible AI frameworks for large-scale operations.",
            "Transforming enterprise decision-making with unified data platforms.",
            "Ensuring compliance and trust in AI-led environments.",
          ],
          speaker: {
            name: "Ananya Desai",
            role: "Chief Data Officer",
            company: "Reliance Industries",
            img: SPEAKER_IMAGES["Ananya Desai"],
          },
        },

        { type: "session", status: STATUS.AVAILABLE, time: "15:30 - 16:00", title: "Available Session" },
        { type: "session", status: STATUS.BOOKED, time: "16:00 - 16:30", title: "Day 2 Closing Remarks" },
      ],
    };

    return { STATUS, day1, day2 };
  }, []);

  const day = activeDay === "day1" ? data.day1 : data.day2;

  return (
    <div className="agendaPage">
      <section className="agendaTop">
        <h1 className="agendaTitle">{meta.pageTitle}</h1>

        <a className="downloadLink" href={meta.downloadUrl} target="_blank" rel="noreferrer">
          {meta.downloadLabel} <span className="dlIcon" aria-hidden>↓</span>
        </a>

        <div className="note">{meta.locationNote}</div>

        <div className="tabs">
          <button className={`tab ${activeDay === "day1" ? "active" : ""}`} onClick={() => setActiveDay("day1")} type="button">
            <div className="tabLabel">{data.day1.label}</div>
            <div className="tabDate">{data.day1.dateLabel}</div>
          </button>

          <button className={`tab ${activeDay === "day2" ? "active" : ""}`} onClick={() => setActiveDay("day2")} type="button">
            <div className="tabLabel">{data.day2.label}</div>
            <div className="tabDate">{data.day2.dateLabel}</div>
          </button>
        </div>

        <div className="legend">
          {day.legend.map((x) => (
            <div className="legendItem" key={x.label}>
              <span className={`dot ${x.type.toLowerCase()}`} />
              <span className="legendText">{x.label}</span>
            </div>
          ))}
        </div>
      </section>

      <main className="agendaContent">
        {day.items.map((item, idx) => {
          if (item.type === "break") {
            return <BreakBlock key={`${item.time}-${idx}`} time={item.time} title={item.title} sub={item.sub} />;
          }
          return (
            <SessionCard
              key={`${item.time}-${idx}`}
              status={item.status}
              time={item.time}
              title={item.title}
              bullets={item.bullets}
              speaker={item.speaker}
            />
          );
        })}
      </main>

      <style>{css}</style>
    </div>
  );
};

/* ----------------------------- UI Pieces ----------------------------- */

const SessionCard = ({ status, time, title, bullets, speaker }) => {
  const statusLabel = statusToLabel(status);

  return (
    <article className={`card ${status.toLowerCase()}`}>
      <div className="cardTop">
        <div className="timeRow">
          <span className="clock" aria-hidden>🕘</span>
          <span className="time">{time}</span>
        </div>

        <div className={`chip ${status.toLowerCase()}`}>
          <span className="chipIcon" aria-hidden>{statusToIcon(status)}</span>
          <span className="chipText">{statusLabel}</span>
        </div>
      </div>

      <div className="cardBody">
        <h3 className="cardTitle">{title}</h3>

        {Array.isArray(bullets) && bullets.length > 0 && (
          <ul className="bullets">
            {bullets.map((b, i) => (
              <li key={i}>{b}</li>
            ))}
          </ul>
        )}

        {speaker && <SpeakerBlock speaker={speaker} />}
      </div>
    </article>
  );
};

const BreakBlock = ({ time, title, sub }) => (
  <section className="breakBlock">
    <div className="breakLeft" />
    <div className="breakMid">
      <div className="breakTitle">{title}</div>
    </div>
    <div className="breakRight">
      <div className="breakTime">
        <span className="clock" aria-hidden>🕘</span> {time}
      </div>
      {sub && <div className="breakSub">{sub}</div>}
    </div>
  </section>
);

const SpeakerBlock = ({ speaker }) => {
  const initials = getInitials(speaker.name || "");
  const hasImg = Boolean(speaker.img);

  return (
    <div className="speaker">
      <div className="speakerLeft">
        {hasImg ? (
          <img className="speakerImg" src={speaker.img} alt={speaker.name} />
        ) : (
          <div className="avatar" aria-hidden>{initials}</div>
        )}
      </div>

      <div className="speakerMid">
        <div className="speakerName">{speaker.name}</div>
        <div className="speakerRole">{speaker.role}</div>
        <div className="speakerCompany">{speaker.company}</div>
      </div>
    </div>
  );
};

/* ----------------------------- Helpers ----------------------------- */

function statusToLabel(status) {
  switch (status) {
    case "SPONSORED":
      return "SPONSORED SESSION";
    case "BOOKED":
      return "BOOKED SESSION";
    case "AVAILABLE":
      return "AVAILABLE SESSION";
    default:
      return String(status || "");
  }
}

function statusToIcon(status) {
  switch (status) {
    case "SPONSORED":
      return "★";
    case "BOOKED":
      return "+";
    case "AVAILABLE":
      return "•";
    default:
      return "•";
  }
}

function getInitials(name) {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  const a = parts[0]?.[0] || "";
  const b = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return (a + b).toUpperCase();
}

/* ----------------------------- CSS ----------------------------- */

const css = `
  :root{
    --bg: #f5f7fb;
    --card: #ffffff;
    --text: #111827;
    --muted: #6b7280;
    --line: #e5e7eb;

    --blue: #2f6fed;
    --blueSoft: rgba(47,111,237,.12);

    --gold: #f2b01e;
    --goldSoft: rgba(242,176,30,.14);

    --slate: #94a3b8;
    --slateSoft: rgba(148,163,184,.14);

    --radius: 14px;
  }

  *{ box-sizing: border-box; }

  .agendaPage{
    width: 100%;
    background: var(--bg);
    padding: 0 0 24px;
  }

  .agendaTop{
    background: #ffffff;
    border-bottom: 1px solid var(--line);
    padding: 18px 18px 12px;
  }

  .agendaTitle{
    margin: 0;
    font-size: 26px;
    font-weight: 1000;
    letter-spacing: .2px;
    color: var(--text);
  }

  .downloadLink{
    display:inline-flex;
    align-items:center;
    gap: 8px;
    margin-top: 8px;
    color: var(--blue);
    text-decoration: none;
    font-weight: 800;
  }
  .downloadLink:hover{ text-decoration: underline; }
  .dlIcon{ font-size: 14px; }

  .note{
    margin-top: 10px;
    font-size: 12px;
    color: var(--muted);
    font-weight: 600;
  }

  .tabs{
    display:flex;
    gap: 10px;
    margin-top: 14px;
    width: 100%;
  }
  .tab{
    flex: 1;
    width: 100%;
    border: 1px solid var(--line);
    background: #fff;
    border-radius: 12px;
    padding: 12px;
    text-align:left;
    cursor:pointer;
  }
  .tabLabel{
    font-weight: 1000;
    font-size: 13px;
    letter-spacing:.35px;
    color: #0b1220;
  }
  .tabDate{
    font-size: 12px;
    color: var(--muted);
    margin-top: 3px;
    font-weight: 600;
  }
  .tab.active{
    border-color: rgba(47,111,237,.55);
    box-shadow: 0 0 0 2px rgba(47,111,237,.10) inset;
  }

  .legend{
    display:flex;
    gap: 16px;
    align-items:center;
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid var(--line);
    flex-wrap: wrap;
  }
  .legendItem{ display:flex; align-items:center; gap:8px; }
  .legendText{ font-size: 12px; color: var(--muted); font-weight: 700; }
  .dot{ width: 8px; height: 8px; border-radius: 99px; display:inline-block; }
  .dot.sponsored{ background: var(--gold); }
  .dot.booked{ background: var(--blue); }
  .dot.available{ background: var(--slate); }

  .agendaContent{
    width: 100%;
    padding: 14px 18px 0;
  }

  .card{
    width: 100%;
    background: var(--card);
    border: 1px solid var(--line);
    border-radius: var(--radius);
    overflow: hidden;
    margin-bottom: 12px;
    box-shadow: 0 6px 18px rgba(17,24,39,.04);
    position: relative;
  }

  .card::before{
    content:"";
    position:absolute; left:0; top:0; bottom:0;
    width: 6px;
    background: var(--slate);
  }
  .card.booked::before{ background: var(--blue); }
  .card.sponsored::before{ background: var(--gold); }
  .card.available::before{ background: var(--slate); }

  .cardTop{
    display:flex;
    justify-content: space-between;
    align-items:center;
    padding: 14px 14px 10px 18px;
    gap: 10px;
  }

  .timeRow{
    display:flex;
    align-items:center;
    gap:8px;
    color: var(--muted);
    font-weight: 800;
    font-size: 12px;
  }
  .clock{ font-size: 14px; }

  .chip{
    display:inline-flex;
    align-items:center;
    gap: 8px;
    border-radius: 10px;
    padding: 7px 10px;
    border: 1px solid var(--line);
    font-weight: 1000;
    font-size: 11px;
    letter-spacing: .2px;
    white-space: nowrap;
  }
  .chipIcon{ font-size: 12px; opacity: .9; }

  .chip.available{ background: var(--slateSoft); color: #334155; }
  .chip.booked{ background: var(--blueSoft); color: #1e3a8a; }
  .chip.sponsored{ background: var(--goldSoft); color: #854d0e; }

  .cardBody{ padding: 0 14px 14px 18px; }
  .cardTitle{
    margin: 0 0 10px 0;
    font-size: 16px;
    line-height: 1.25;
    font-weight: 1000;
    color: #0b1220;
  }

  .bullets{
    margin: 0;
    padding-left: 18px;
    color: #334155;
  }
  .bullets li{
    margin: 8px 0;
    line-height: 1.35;
    font-size: 13px;
    font-weight: 600;
  }

  /* Speaker block (NO LOGO) */
  .speaker{
    margin-top: 12px;
    display:flex;
    gap: 12px;
    align-items:center;
    background: #fbfcff;
    border: 1px solid var(--line);
    border-radius: 12px;
    padding: 10px;
  }

  .speakerLeft{ display:flex; align-items:center; }

  .speakerImg{
    width: 44px;
    height: 44px;
    border-radius: 999px;
    object-fit: cover;
    border: 2px solid rgba(15,23,42,.18);
    background: #fff;
  }

  .avatar{
    width: 44px; height: 44px;
    border-radius: 999px;
    display:flex; align-items:center; justify-content:center;
    background: rgba(47,111,237,.10);
    border: 1px solid rgba(47,111,237,.18);
    font-weight: 1000;
    color: #1e3a8a;
  }

  .speakerMid{ flex: 1; min-width: 0; }
  .speakerName{ font-weight: 1000; font-size: 13px; color: #0b1220; }
  .speakerRole{ font-size: 12px; color: var(--muted); margin-top: 2px; font-weight: 700; }
  .speakerCompany{ font-size: 12px; color: #334155; font-weight: 800; margin-top: 2px; }

  .breakBlock{
    width: 100%;
    background: #ffffff;
    border: 1px solid var(--line);
    border-radius: var(--radius);
    display:flex;
    overflow:hidden;
    margin: 12px 0;
    box-shadow: 0 6px 18px rgba(17,24,39,.04);
  }
  .breakLeft{ width: 8px; background: var(--gold); }
  .breakMid{
    padding: 12px;
    flex: 1;
    display:flex;
    align-items:center;
  }
  .breakTitle{ font-weight: 1000; letter-spacing: .2px; color: #0b1220; }
  .breakRight{
    padding: 10px 12px;
    border-left: 1px solid var(--line);
    display:flex;
    flex-direction: column;
    justify-content: center;
    min-width: 170px;
    text-align: right;
  }
  .breakTime{ font-weight: 1000; color: #334155; }
  .breakSub{ font-size: 12px; color: var(--muted); margin-top: 2px; font-weight: 700; }

  @media (max-width: 620px){
    .tabs{ flex-direction: column; }
    .breakRight{ min-width: 0; }
  }
`;

export default Agenda;